<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { ColorBaseToken, NavigationContainerProps } from '@core/types'
import { computed, navigateTo, ref, trackJoinNowClick, useColorScheme, useCoreConfig, useDefaults, useDevice, useGameSearchOverlay, useGuard, useI18n, useLinks, useNavigationFilter, usePasskeyLoginFlow, usePromotionConfig, useRewardsClient, useRoute, useThemeLogo, useToggle, useTracking, useUserAuthClient, useUserInfo, useUsername, useUserNotifications, watch } from '#imports'

const _props = withDefaults(defineProps<NavigationContainerProps>(), {
    navigation: undefined,
    navbarTop: undefined,
    signupButtonShiny: undefined,
})
const props = useDefaults({
    props: _props,
    defaults: {
        signupButtonShiny: 'snake',
    },
})

const { data: user, isLoggedIn, isExisting } = useUserInfo()
const { username, isLoading: isUsernameLoading } = useUsername()
const { isVisible, resolveHref, resolveTitle } = useNavigationFilter()
const { site } = useCoreConfig()

// 'nav' is a brand colour token core's ColorBaseToken union does not enumerate.
const signupButtonColor = computed<ColorBaseToken | undefined>(() => (isExisting.value ? 'nav' as ColorBaseToken : undefined))

const menuLogo = useThemeLogo('menu')
const iconLogo = useThemeLogo('icon')
const { useLogout } = useUserAuthClient()
const { mutate: logout } = useLogout()
const { isOpen, toggle: toggleSearch } = useGameSearchOverlay()
const { isMobile } = useDevice()
const links = useLinks()
const { handleLoginClick } = usePasskeyLoginFlow()
const { trackEvent } = useTracking()
const { can } = useGuard()
const { pages } = usePromotionConfig()
const { useGetActiveRewards, useGetEnqueuedRewards } = useRewardsClient()
const { data: activeRewards } = useGetActiveRewards({ enabled: isLoggedIn })
const { data: enqueuedRewards } = useGetEnqueuedRewards({ enabled: isLoggedIn })
const bonusCount = computed(() => (activeRewards.value?.length ?? 0) + (enqueuedRewards.value?.length ?? 0))
const { t } = useI18n()
const route = useRoute()
const { isEnabled: colorSchemeEnabled } = useColorScheme()

const navigation = computed(() => {
    return (props.navigation ?? [])
        .filter(isVisible)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .flatMap((link) => {
            const href = resolveHref(link)
            if (href === undefined) return []

            return {
                active: route.path === href,
                name: href,
                path: href,
                title: resolveTitle(link),
                external: link.external,
                href,
                onClick: link.key === 'winners' || href === t('pages.winners') ? handleWinnersClick : undefined,
            }
        })
})

const displayName = computed<string>(() => user.value?.fullName ?? `${user.value?.firstName ?? ''} ${user.value?.lastName ?? ''}`.trim())

const menuLogoWidth = computed(() => Number(menuLogo.value?.width) || undefined)
const swapLogo = computed(() => isLoggedIn.value && !!menuLogoWidth.value && !!iconLogo.value)
const logoStyle = computed(() => swapLogo.value ? { '--nav-logo-menu-width': `${menuLogoWidth.value}px` } : undefined)

function handleWinnersClick() {
    trackEvent({
        event_name: 'site-nav_winners_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleBonusesClick() {
    trackEvent({
        event_name: 'site-nav_bonuses_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    return navigateTo(t('pages.myBonuses'))
}

async function handleDepositClick(event: MouseEvent) {
    trackEvent({
        event_name: 'site-nav_deposit_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    const isAllowed = await can()

    if (!isAllowed) {
        event.preventDefault()

        return
    }

    await navigateTo(t('pages.deposit'), { external: true })
}

function handleWithdrawalClick() {
    trackEvent({
        event_name: 'site-nav_withdrawal_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleMyAccountClick() {
    trackEvent({
        event_name: 'site-nav_my-account_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleSavedGamesClick() {
    trackEvent({
        event_name: 'site-nav_saved-games_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleSupportClick() {
    trackEvent({
        event_name: 'site-nav_support_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })
}

function handleLogoutClick() {
    trackEvent({
        event_name: 'site-nav_logout_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    // Body is ignored; `undefined` satisfies the required-argument signature of `mutate`.
    logout(undefined)
}

// Anchor for the desktop user-menu dropdown
const userMenuActivator = ref<ComponentPublicInstance | null>(null)

// User notifications
const [userNotificationsDrawerOpen, toggleUserNotificationsDrawer] = useToggle(false)

const { userNotifications, refresh: refreshNotificationQuery, markAsRead, useCallAction, getIcon, errors } = useUserNotifications({ enabled: isLoggedIn, exclude_expired: true, useBaseQueryKey: true })
const unreadNotificationsCount = computed(() => {
    return (userNotifications?.value ?? []).filter(notification => !notification.read).length
})

function goToNotificationsPage() {
    closeUserNotificationsDrawer()

    navigateTo(pages.notifications)
}

function closeUserNotificationsDrawer() {
    userNotificationsDrawerOpen.value = false
}

function toggleNotifications(userMenuOpen: boolean, toggleUserMenu: () => void): void {
    toggleUserNotificationsDrawer()
    if (userMenuOpen) {
        toggleUserMenu()
    }
}

const mutations = {
    callAction: useCallAction()
}

watch(() => userNotificationsDrawerOpen.value, (isOpen) => {
    if (!isOpen) {
        refreshNotificationQuery()
    }
})
</script>

<template>
    <UIPageNavigation
        :navigation="navigation"
        :user-menu-enabled="isLoggedIn"
        :navbar-top="props.navbarTop"
    >
        <template #brand>
            <NuxtLink
                :to="isExisting ? t('pages.lobby') : '/'"
                :aria-label="t('core.components.NavigationContainer.brandAriaLabel')"
                class="nav__logo"
                :class="{ 'nav__logo--swap': swapLogo }"
                :style="logoStyle"
            >
                <span class="nav__logo-room">
                    <NuxtImg
                        v-if="menuLogo"
                        v-bind="menuLogo"
                        class="nav__logo-menu"
                        decoding="async"
                        preset="local"
                        provider="cloudflare"
                        :alt="`${site.title} logo`"
                    />
                    <NuxtImg
                        v-if="iconLogo && (swapLogo || !menuLogo)"
                        v-bind="iconLogo"
                        class="nav__logo-icon"
                        decoding="async"
                        preset="local"
                        provider="cloudflare"
                        :alt="`${site.title} logo`"
                    />
                </span>
            </NuxtLink>
        </template>

        <template
            #user-actions="{
                state: { userMenuOpen },
                methods: { toggleUserMenu, closeUserMenu },
            }"
        >
            <GameSearchOverlayHost />

            <template v-if="!isLoggedIn">
                <button
                    class="nav__search-btn"
                    data-test="game-search-button"
                    :aria-label="t('core.components.NavigationContainer.searchAriaLabel')"
                    @click="toggleSearch"
                >
                    <UIIcon name="search" size="20" />
                </button>

                <UIButton
                    v-bind="links.signup.props()"
                    id="nav-create-account-button"
                    data-test="navigation-container-button-signup"
                    :as="isExisting ? 'nav' : 'cta'"
                    :color="signupButtonColor"
                    :size="isMobile ? 'xs' : 'sm'"
                    :shiny="isExisting ? false : props.signupButtonShiny"
                    glitch
                    @click="trackJoinNowClick('site-nav')"
                >
                    {{ isMobile ? t('core.components.NavigationContainer.signupShort') : t('core.components.NavigationContainer.signup') }}
                </UIButton>

                <UIButton
                    id="nav-login-account-button"
                    data-test="navigation-container-button-login"
                    :as="isExisting ? 'cta' : 'nav'"
                    :size="isMobile ? 'xs' : 'sm'"
                    @click="handleLoginClick"
                >
                    {{ t('core.components.NavigationContainer.login') }}
                </UIButton>
            </template>

            <template v-else>
                <PaymentsDepositButton id="nav-deposit-button" />

                <button
                    class="nav__search-btn nav__search-btn--chip"
                    :class="{ 'nav__search-btn--active': isOpen }"
                    data-test="game-search-button"
                    :aria-label="t('core.components.NavigationContainer.searchAriaLabel')"
                    @click="toggleSearch"
                >
                    <UIIcon name="search" size="20" />
                </button>

                <UIDrawer
                    v-model="userNotificationsDrawerOpen"
                    :width="360"
                    :max-height="600"
                    :top="48"
                    @close="closeUserNotificationsDrawer"
                >
                    <template #activator>
                        <button
                            :class="[
                                'nav__notifications-menu-btn',
                                'nav__notifications-menu-btn-happytiger',
                                { 'nav__notifications-menu-btn--active': false, 'nav__notifications-menu-btn--unread': unreadNotificationsCount && userNotifications?.length },
                            ]"
                            data-test="label-open-notifications"
                            @click.stop="toggleNotifications(userMenuOpen, toggleUserMenu)"
                        >
                            <UIIcon
                                name="bell"
                                size="20"
                            />
                        </button>
                    </template>

                    <template #header>
                        <h2 class="nav__notifications-header">
                            {{ t('core.components.NavigationContainer.messagesHeader') }}
                        </h2>
                    </template>

                    <template #body>
                        <template v-if="userNotifications?.length">
                            <PromotionUserNotificationCard
                                v-for="notification in userNotifications"
                                :key="notification.id"
                                :notification="notification"
                                :errors="errors"
                                :icon="getIcon(notification.type || 'bell')"
                                class="nav__notifications-card"
                                @call-action="mutations.callAction.mutate"
                                @mark-as-read="markAsRead.mutate({
                                    notifications: [notification]
                                })"
                            />
                        </template>
                        <template v-else>
                            <div class="nav-notifications__empty">
                                <div class="nav-notifications__empty-content">
                                    <p class="nav-notifications__empty-title">
                                        {{ t('promotions.notifications.noNotifications') }}...
                                    </p>
                                    <span class="nav-notifications__empty-bar" aria-hidden="true" />
                                    <span class="nav-notifications__empty-bar nav-notifications__empty-bar--short" aria-hidden="true" />
                                </div>
                                <div class="nav-notifications__empty-icon">
                                    <UIIcon name="bell" size="24" />
                                </div>
                            </div>
                        </template>
                    </template>

                    <template v-if="userNotifications?.length" #footer>
                        <UIButton as="nav" class="nav__notifications-link" @click="goToNotificationsPage">
                            {{ t('promotions.notifications.seeAllNotifications') }} ({{ userNotifications?.length }})
                        </UIButton>
                    </template>
                </UIDrawer>

                <UIUserAvatar
                    ref="userMenuActivator"
                    :username="username ?? ''"
                    :display-name="displayName"
                    data-test="navigation-container-button-dropdown"
                    @click.stop="toggleUserMenu()"
                />

                <UIUserMenuPanel
                    :model-value="userMenuOpen"
                    :activator="userMenuActivator"
                    :username="username"
                    :username-loading="isUsernameLoading"
                    :display-name="displayName"
                    :bonus-count="bonusCount"
                    @update:model-value="(val: boolean) => !val && closeUserMenu()"
                    @deposit="handleDepositClick"
                    @bonuses="handleBonusesClick"
                    @logout="handleLogoutClick"
                >
                    <UIListLink
                        :href="t('pages.home')"
                        prepend-icon="partner-profile"
                        append-icon="arrow-right"
                        @click="handleMyAccountClick"
                    >
                        {{ t('core.components.NavigationContainer.myAccount') }}
                    </UIListLink>
                    <UIListLink
                        :href="t('pages.savedGames')"
                        prepend-icon="saved-games"
                        append-icon="arrow-right"
                        @click="handleSavedGamesClick"
                    >
                        {{ t('core.components.NavigationContainer.savedGames') }}
                    </UIListLink>
                    <UIListLink
                        :href="t('pages.withdraw')"
                        prepend-icon="payout"
                        append-icon="arrow-right"
                        @click="handleWithdrawalClick"
                    >
                        {{ t('core.components.NavigationContainer.withdraw') }}
                    </UIListLink>
                    <UIListLink
                        v-bind="links.support.props()"
                        prepend-icon="support"
                        append-icon="arrow-right"
                        @click="handleSupportClick"
                    >
                        {{ t('core.components.NavigationContainer.support') }}
                    </UIListLink>

                    <template v-if="colorSchemeEnabled" #footerLeading>
                        <UIColorSchemeSwitch />
                    </template>
                </UIUserMenuPanel>
            </template>
        </template>

        <template #user-menu>
            <UIListLink
                :href="t('pages.home')"
                class="nav__user-menu-link"
                prepend-icon="partner-profile"
                @click="handleMyAccountClick"
            >
                {{ t('core.components.NavigationContainer.myAccount') }}
            </UIListLink>
            <UIListLink
                class="nav__user-menu-link"
                prepend-icon="credits"
                @click.prevent="handleDepositClick"
            >
                {{ t('core.components.NavigationContainer.deposit') }}
            </UIListLink>
            <UIListLink
                :href="t('pages.withdraw')"
                class="nav__user-menu-link"
                prepend-icon="payout"
                @click="handleWithdrawalClick"
            >
                {{ t('core.components.NavigationContainer.withdraw') }}
            </UIListLink>
            <UIListLink
                :href="t('pages.savedGames')"
                class="nav__user-menu-link"
                prepend-icon="saved-games"
                @click="handleSavedGamesClick"
            >
                {{ t('core.components.NavigationContainer.savedGames') }}
            </UIListLink>
            <UIListLink
                :href="t('pages.myBonuses')"
                class="nav__user-menu-link"
                prepend-icon="bonus"
                @click.prevent="handleBonusesClick"
            >
                {{ t('core.components.NavigationContainer.myBonuses') }}
            </UIListLink>
            <UIListLink
                v-bind="links.support.props()"
                class="nav__user-menu-link"
                prepend-icon="support"
                @click="handleSupportClick"
            >
                {{ t('core.components.NavigationContainer.support') }}
            </UIListLink>
            <UIListLink
                href="#"
                class="nav__user-menu-link"
                prepend-icon="logout"
                data-cy="a-user-nav-logout"
                @click.prevent="handleLogoutClick"
            >
                {{ t('core.components.NavigationContainer.logout') }}
            </UIListLink>
        </template>
    </UIPageNavigation>
</template>

<style scoped lang="scss">
.nav__logo {
    display: flex;
    align-items: center;

    &-room {
        display: flex;
        align-items: center;
    }

    &-menu,
    &-icon {
        flex: none;
    }

    &--swap {
        @media (max-width: #{$screen-md - 1}) {
            flex: 1 1 0;
            min-width: 0;
            padding-right: $gutter-size;

            .nav__logo-room {
                container: nav-logo-room / inline-size;
                width: calc(100% - var(--nav-logo-menu-width) + 1px);
            }

            .nav__logo-menu {
                display: none;
            }

            @container nav-logo-room (min-width: 1px) {
                .nav__logo-menu {
                    display: block;
                }

                .nav__logo-icon {
                    display: none;
                }
            }
        }

        @media (min-width: $screen-md) {
            .nav__logo-icon {
                display: none;
            }
        }
    }
}

.nav__notifications-menu-btn {
    display: flex;
    justify-items: center;
    aspect-ratio: 1 / 1;
    height: 36px;
    border-radius: calc(var(--border-radius, $border-radius) * 2);
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        @include nav-action-hover;
    }
}

.nav-notifications__empty {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 25px;
    background-color: var(--notification-card-bg, var(--background));
    border-radius: var(--border-radius, $border-radius);

    &-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 10px;
        min-width: 0;
    }

    &-title {
        margin: 0;
        font-size: $font-size-base;
        color: var(--on-background, $notification-font-color);
    }

    &-bar {
        height: 8px;
        background-color: var(--on-background, $notification-font-color);
        border-radius: 4px;
        opacity: 0.2;

        &--short {
            width: 60%;
        }
    }

    &-icon {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background-color: var(--notification-card-icon-bg, var(--surface));
        color: var(--notification-card-icon-color, var(--secondary));
        border-radius: 50%;
    }
}

.nav__notifications {
    &-menu-btn {
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: 1fr;
        justify-items: center;
        align-items: center;
        color: var(--nav-icon-color, $nav-icon-color);
        font-size: $font-size-xs;
        font-weight: $text-regular;
        background-color: var(--surface);
        border: none;
        white-space: nowrap;
        text-shadow: none;
        text-decoration: none;

        &::before {
            content: '';
            position: absolute;
            top: 3px;
            right: -2px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: transparent;
            transition: all 0.3s ease-in-out;
        }

        .icon {
            margin-left: 1px;
        }

        &--unread {
            position: relative;

            .icon {
                animation: wiggleScale 3.5s ease-in-out infinite;
            }

            &::before {
                content: '';
                position: absolute;
                top: -2px;
                right: -2px;
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background-color: $notification-dot-color;
            }
        }
    }

    &-header {
        font-size: $font-size-lg;
        font-weight: $text-bold;
        padding: 0;
        margin: 0;
    }

    &-card {
        margin-bottom: 15px;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

    &-link {
        display: flex;
        margin: 0 auto;
    }
}

@keyframes wiggleScale {
    0% {
        transform: rotate(0) scale(1);
    }
    4%,
    8% {
        transform: rotate(0) scale(1.1);
    }
    12% {
        transform: rotate(10deg) scale(1.1);
    }
    16% {
        transform: rotate(-7deg) scale(1.1);
    }
    20% {
        transform: rotate(5deg) scale(1.1);
    }
    24% {
        transform: rotate(-3deg) scale(1.1);
    }
    28% {
        transform: rotate(1deg) scale(1.1);
    }
    32%,
    36% {
        transform: rotate(0) scale(1.1);
    }
    40% {
        transform: rotate(0) scale(1);
    }
    100% {
        transform: rotate(0) scale(1);
    }
}

.nav__user-menu-link {
    padding: 0 15px;
    cursor: pointer;

    @media (min-width: $screen-md) {
        padding: 0 20px;
    }
}

.nav__user-actions .btn--outlined {
    border-color: var(--border-secondary-soft, #93c0df);

    &:hover,
    &:focus,
    &:active {
        border-color: var(--secondary, $link-color);
    }
}

.nav__search-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    height: 36px;
    border-radius: calc(var(--border-radius, #{$border-radius}) * 2);
    color: var(--nav-icon-color, $nav-icon-color);
    background: transparent;
    border: none;
    cursor: pointer;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:not(.nav__search-btn--chip):hover {
        color: var(--nav-icon-color-hover, $nav-icon-color-hover);
        background: var(--nav-button-bg-hover, $nav-button-color-hover);
    }

    &--chip {
        background: var(--surface);
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);
        transition:
            box-shadow 0.2s ease-in-out,
            background 0.15s ease,
            color 0.15s ease;

        &:hover {
            @include nav-action-hover;
        }
    }

    &--active {
        background: var(--primary);
        color: var(--on-primary);
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);

        &:hover {
            background: var(--primary);
            color: var(--on-primary);
            box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.17);
        }
    }
}
</style>
